function isValidCoordinate(lat: number, lon: number): boolean {
    return (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat >= -90 &&
        lat <= 90 &&
        lon >= -180 &&
        lon <= 180
    );
}

type OsmAddress = {
    suburb?: string;
    neighbourhood?: string;
    quarter?: string;
    hamlet?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
    county?: string;
};

/**
 * City-level place name only — never suburbs or neighbourhoods.
 */
export function formatOsmCityName(
    address: OsmAddress | undefined,
    displayName?: string,
): string | null {
    if (address) {
        const city =
            address.city ??
            address.town ??
            address.village ??
            address.municipality ??
            address.county ??
            address.hamlet;
        if (city) return city;
    }

    if (displayName) {
        const firstPart = displayName.split(",")[0]?.trim();
        if (firstPart) return firstPart;
    }

    return null;
}

/**
 * @deprecated Use formatOsmCityName for forecast location labels.
 */
export function formatOsmPlaceName(
    address: OsmAddress | undefined,
    displayName?: string,
): string | null {
    return formatOsmCityName(address, displayName);
}

/**
 * Reverse geocode coordinates using OpenStreetMap Nominatim API.
 * Returns a city-level name when available.
 */
export async function reverseGeocode(lat: number, lon: number): Promise<string | null> {
    if (!isValidCoordinate(lat, lon)) {
        return null;
    }

    try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=8&addressdetails=1`;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(url, {
            headers: { "User-Agent": "AuroraForecast/1.0" },
            signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (response.status === 429) {
            console.warn("Nominatim rate limited (429)");
            return null;
        }
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
            return null;
        }

        return formatOsmCityName(data.address, data.display_name);
    } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
            console.warn("Reverse geocode request timed out");
        } else {
            console.error("Error reverse geocoding:", error);
        }
        return null;
    }
}
